import { StarFilled, StarOutlined } from "@ant-design/icons";
import { message, Rate } from "antd";
import { useState } from "react";

import { IconGiveFeedback, IconX } from "@btnet/blueprint-icons";
import { BTButton } from "@btnet/core-components/BTButton";
import { BTPopover } from "@btnet/core-components/BTPopover";
import { BTText } from "@btnet/core-components/BTText";
import { BTTextArea } from "@btnet/core-components/BTTextArea";
import { showApixErrorMessage } from "@btnet/services-btnet/apix-handler";
import { useFeedbackSubmitPostV2 } from "@btnet/services-btnet/handlers/feedback/feedback";
import type { FeedbackTarget } from "@btnet/services-btnet/handlers/models/feedbackTarget";

import yup from "utilities/form/yup";

import { BTItemHeader } from "commonComponents/btWrappers/BTItemHeader/BTItemHeader";
import { type TBTItemHeaderIcon } from "commonComponents/btWrappers/BTItemHeader/BTItemHeader.types";
import { BTModal } from "commonComponents/btWrappers/BTModal/BTModal";
import {
    type IBTInjectedFormikProps,
    withFormikBT,
} from "commonComponents/utilities/Formik/WithFormikBT";

import "./FeedbackWidget.less";

interface IFeedbackWidgetProps {
    feedbackTarget: FeedbackTarget;
    ratingMode?: "hidden" | "optional" | "required";
    onClose: () => void;
    onSubmitSuccess?: () => void;
    extraInfo?: string;
    feedbackText?: string;
}

export interface IFeedbackWidgetFormValues {
    rating: number;
    feedbackText: string;
}

function FeedbackWidgetInternal(
    props: IBTInjectedFormikProps<IFeedbackWidgetProps, IFeedbackWidgetFormValues>,
) {
    const {
        ratingMode = "optional",
        onClose,
        onSubmitSuccess,
        values,
        setFieldValue,
        setFieldTouched,
        isValid,
        feedbackTarget,
        extraInfo,
    } = props;
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const postFeedbackData = useFeedbackSubmitPostV2({
        mutation: {
            onError: (error) => {
                showApixErrorMessage(error);
            },
            onSuccess: () => {
                void message.success("Thank you for your feedback!", 5);
                onSubmitSuccess?.();
                onClose();
            },
        },
    });

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            await postFeedbackData.mutateAsync({
                data: {
                    feedbackText: values.feedbackText,
                    rating: values.rating,
                    feedbackTarget: feedbackTarget,
                    extraInfo: extraInfo,
                },
            });
        } catch {
            // onError already showed the error; re-enable Submit so the user can retry. Not a
            // finally: on success onClose unmounts this component.
            setIsSubmitting(false);
        }
    };

    // Kept as a `customComponent` rather than the mapped `closeX` icon so the widget keeps its
    // existing `close` test id and the Esc hotkey the modal itself does not supply (BTModal is
    // rendered with `removeHeader`, which turns off its own `keyboard` handling).
    const headerIconActions: TBTItemHeaderIcon[] = [
        {
            type: "customComponent",
            id: "feedback-close",
            pinned: true,
            customComponent: (
                <BTPopover content="Close">
                    <BTButton
                        data-testid="close"
                        type="tertiary"
                        aria-label="close"
                        hotkey="close"
                        icon={<IconX size="md" />}
                        onClick={onClose}
                    />
                </BTPopover>
            ),
        },
    ];

    const feedbackFooter = (
        <div className="flex flex-row gap-2 FeedbackWidget__footer">
            <BTButton data-testid="feedback-cancel-button" type="secondary" onClick={onClose}>
                Cancel
            </BTButton>
            <BTPopover content={!isValid ? "Please fill out all required fields." : undefined}>
                {/* A disabled button emits no pointer events, so the popover needs a
                    live wrapper to hang the trigger on. */}
                <span>
                    <BTButton
                        data-testid="feedback-submit-button"
                        type="primary"
                        onClick={handleSubmit}
                        disabled={isSubmitting || !isValid}
                    >
                        Submit feedback
                    </BTButton>
                </span>
            </BTPopover>
        </div>
    );

    return (
        <BTModal
            visible
            beforeClose={onClose}
            claimNativeBack
            width="480px"
            data-testid="feedback-modal"
            footer={feedbackFooter}
            removeHeader
            removeBodyPadding
            title="In-App Feedback"
        >
            <BTItemHeader
                testid="feedback-modal-header"
                ariaLabel="Tell us what you think"
                className="FeedbackWidget__header"
            >
                {/* Same shape as the other swapped headers: a `min-w-0` title column with the
                    actions as its flex sibling. No job name on this surface, so the column holds
                    the icon and title only. */}
                <div className="flex flex-row items-center gap-2">
                    <div className="flex flex-row items-center gap-2 min-w-0">
                        <IconGiveFeedback size="lg" className="shrink-0" />
                        <BTText
                            size="heavy-md"
                            tag="h2"
                            testid="feedback-modal-title"
                            className="truncate"
                        >
                            Tell us what you think
                        </BTText>
                    </div>
                    <BTItemHeader.ActionButtons
                        testid="feedback-modal-header-actions"
                        className="w-auto grow py-0!"
                        iconActions={headerIconActions}
                    />
                </div>
            </BTItemHeader>
            <div className="FeedbackWidget__content">
                <BTText size="normal-md">
                    {props.feedbackText
                        ? props.feedbackText
                        : "We're improving this area and want your feedback. Share what worked, what didn't, or what you expected to see."}
                </BTText>

                {ratingMode !== "hidden" && (
                    <div className="FeedbackWidget__rating">
                        <Rate
                            value={values.rating}
                            onChange={(value) => setFieldValue("rating", value)}
                            character={
                                <>
                                    <StarOutlined className="FeedbackWidget__star-outlined" />
                                    <StarFilled className="FeedbackWidget__star-filled" />
                                </>
                            }
                        />
                        {values.rating > 0 && (
                            <BTText size="normal-md" className="FeedbackWidget__rating-label">
                                {`${values.rating} out of 5 stars`}
                            </BTText>
                        )}
                    </div>
                )}

                <div className="FeedbackWidget__feedback">
                    <BTText size="distinct-sm">Your feedback</BTText>
                    <BTTextArea
                        id="feedbackText"
                        data-testid="feedback-textarea"
                        className="FeedbackWidget__textarea"
                        value={values.feedbackText}
                        onChange={(_, value: string) => setFieldValue("feedbackText", value)}
                        onBlur={setFieldTouched}
                        debouncingContext={{ useDebouncing: false }}
                        rows={4}
                        minRows={4}
                        maxLength={1000}
                    />
                </div>
            </div>
        </BTModal>
    );
}

const FeedbackWidgetValidationSchema = (ratingMode: "hidden" | "optional" | "required") =>
    yup.object().shape<IFeedbackWidgetFormValues>({
        rating:
            ratingMode === "required"
                ? yup.number().min(1, "Rating is required").required().label("Rating")
                : yup.number().notRequired().label("Rating"),
        feedbackText: yup
            .string()
            .required("Feedback is required")
            .test("not-empty", "Feedback cannot be empty", (value: string | undefined) => {
                return typeof value === "string" && value.trim().length > 0;
            })
            .max(1000, "Maximum 1000 characters")
            .label("Your feedback"),
    });

export default withFormikBT<IFeedbackWidgetProps, IFeedbackWidgetFormValues>({
    mapPropsToValues: () => ({
        rating: 0,
        feedbackText: "",
    }),
    validationSchema: (props: IFeedbackWidgetProps) =>
        FeedbackWidgetValidationSchema(props.ratingMode ?? "optional"),
    validateOnChange: true,
    validateOnBlur: true,
    handleSubmit: (_values, _formikBag) => {
        // submission handled in the Submit button onClick
    },
})(FeedbackWidgetInternal);
